// /coupons — panel de cupones de días gratis. Sección Cupones con niveles
// read < create < revoke: el listado se ve con `read`, el formulario de crear
// aparece con `create` y el botón de anular con `revoke` (el backend revalida
// cada uno). Es gateo de UX: quien no llega al nivel no ve el control, y el
// endpoint igual lo rechazaría.
//
// Dos modos de creación: cupón único y lote (hasta 500). Al crear, los códigos
// se muestran en pantalla y se pueden bajar en PDF (client-side, mismo patrón
// que el reporte financiero). Los códigos siguen visibles en el listado —
// el PDF es para imprimir/repartir, no la única copia.
//
// Anular: un cupón sin usar deja de ser canjeable. Uno YA CANJEADO también se
// anula (mismo nivel `revoke`), pero eso le saca a la persona el acceso que el
// cupón le dio: su suscripción pasa al plan gratuito del partner (o expira si
// no hay gratuito). Por eso el diálogo de un canjeado pide motivo y lo advierte.
// El backend decide bajo lock si la suscripción todavía vive del cupón; si la
// persona pagó o cambió de plan después, solo se anula el cupón. El resultado
// se muestra con el texto de `describeRevoke`.
//
// Un cupón VIGENTE cuyo código no viaja (el backend lo manda `code: null` al
// nivel de solo lectura, porque un código `issued` es canjeable por quien lo
// lea) se dibuja enmascarado en vez de dejar la celda vacía.

import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCoupons,
  listCoupons,
  revokeCoupon,
  listPartners,
  listPartnerPlans,
  AdminApiError,
  type AdminCoupon,
  type CouponBatchResult,
  type CouponRevokeResult,
  type CouponStatus,
} from '@/lib/api';
import { downloadCouponsPdf } from '@/lib/couponsPdf';
import { useSession } from '@/hooks/useSession';
import { canLevel } from '@/lib/access';
import ConfirmDialog from '@/components/ConfirmDialog';
import { RetryNotice, type RetryNoticeKind } from '@/components/ChargeRetry';
import { absoluteTime } from '@/lib/format';
import { fieldCls } from '@/lib/form-styles';

/** Lo que se dibuja cuando el backend no manda el código de un cupón vigente. */
const MASKED_CODE = '••••••••';

const STATUS_BADGE: Record<CouponStatus, { label: string; cls: string }> = {
  issued: { label: 'sin usar', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  redeemed: { label: 'canjeado', cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300' },
  revoked: { label: 'anulado', cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
};

// El backend acepta hasta 500 por página; 200 ya es una tabla larga de leer.
const PAGE_SIZES = [25, 50, 100, 200] as const;
const DEFAULT_PAGE_SIZE = 50;

/** Texto del resultado de anular. Un `outcome` nuevo del backend no compila hasta tener su texto. */
function describeRevoke(r: CouponRevokeResult, code: string): { kind: RetryNoticeKind; text: string } {
  const until = r.access_until ? absoluteTime(r.access_until) : null;
  switch (r.outcome) {
    case 'revoked_unused':
      return { kind: 'ok', text: `Cupón ${code} anulado: ya no se puede canjear.` };
    case 'already_revoked':
      return { kind: 'info', text: `El cupón ${code} ya estaba anulado. No se cambió nada.` };
    case 'revoked_moved_to_free':
      return {
        kind: 'warn',
        text: `Cupón ${code} anulado. La persona perdió el acceso que le daba el cupón${until ? ` (${until})` : ''} y quedó en el plan gratuito del partner.`,
      };
    case 'revoked_expired':
      return {
        kind: 'warn',
        text: `Cupón ${code} anulado. La persona perdió el acceso que le daba el cupón${until ? ` (${until})` : ''}. El partner no tiene plan gratuito vigente, así que la suscripción quedó expirada.`,
      };
    case 'revoked_subscription_untouched':
      return {
        kind: 'info',
        text: `Cupón ${code} anulado. La suscripción NO se tocó: ya no dependía de este cupón (pagó, cambió de plan, venció o canjeó otro cupón después).`,
      };
  }
}

export default function Coupons(): JSX.Element {
  const session = useSession();
  const queryClient = useQueryClient();

  // Niveles de la sección Cupones (read < create < revoke). `create` habilita
  // el formulario; `revoke` el botón de anular. `canLevel` ya cubre la cadena:
  // quien tiene `revoke` también tiene `create`.
  const canCreate = canLevel(session, 'coupons', 'create');
  const canRevoke = canLevel(session, 'coupons', 'revoke');

  // --- Form de creación ---
  const [mode, setMode] = useState<'single' | 'batch'>('single');
  const [partnerId, setPartnerId] = useState('');
  const [planId, setPlanId] = useState('');
  const [freeDays, setFreeDays] = useState('30');
  const [quantity, setQuantity] = useState('10');
  const [note, setNote] = useState('');
  const [formErr, setFormErr] = useState<string | null>(null);
  const [created, setCreated] = useState<CouponBatchResult | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);

  // --- Filtros del listado ---
  const [fltPartner, setFltPartner] = useState('');
  const [fltStatus, setFltStatus] = useState('');
  const [fltCode, setFltCode] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const [toRevoke, setToRevoke] = useState<AdminCoupon | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeNotice, setRevokeNotice] = useState<{ kind: RetryNoticeKind; text: string } | null>(null);

  const partnersQ = useQuery({ queryKey: ['admin-partners'], queryFn: listPartners });
  const plansQ = useQuery({
    queryKey: ['admin-partner-plans', partnerId],
    queryFn: () => listPartnerPlans(partnerId),
    enabled: partnerId !== '',
  });

  const listQ = useQuery({
    queryKey: ['admin-coupons', fltPartner, fltStatus, fltCode, page, pageSize],
    queryFn: () =>
      listCoupons({
        ...(fltPartner ? { partner_id: fltPartner } : {}),
        ...(fltStatus ? { status: fltStatus as CouponStatus } : {}),
        ...(fltCode.trim() ? { code: fltCode.trim().toUpperCase() } : {}),
        limit: pageSize,
        offset: page * pageSize,
      }),
    // Al cambiar de página se sigue viendo la anterior hasta que llega la
    // nueva: sin esto la tabla desaparece y el paginador salta de lugar.
    placeholderData: keepPreviousData,
  });

  // Reset de página cuando cambia un filtro o el tamaño: quedarse en la página
  // 4 de un resultado que ahora tiene una sola es una pantalla vacía.
  function changeFilter(fn: () => void): void {
    fn();
    setPage(0);
  }

  const total = listQ.data?.total ?? 0;
  const maxPage = Math.max(0, Math.ceil(total / pageSize) - 1);
  const shown = listQ.data?.coupons.length ?? 0;

  // Anular con el filtro "sin usar" puesto achica el resultado: si la página
  // actual quedó más allá del final, retrocede a la última que existe.
  useEffect(() => {
    if (listQ.data && !listQ.isPlaceholderData && page > maxPage) setPage(maxPage);
  }, [listQ.data, listQ.isPlaceholderData, page, maxPage]);

  const createM = useMutation({
    mutationFn: createCoupons,
    onSuccess: (res) => {
      setCreated(res);
      setCopied(false);
      void queryClient.invalidateQueries({ queryKey: ['admin-coupons'] });
    },
    onError: (e) => {
      setFormErr(e instanceof AdminApiError ? e.message : 'No se pudieron crear los cupones.');
    },
  });

  const revokeM = useMutation({
    mutationFn: (c: AdminCoupon) => revokeCoupon(c.coupon_id, revokeReason.trim() || undefined),
    onSuccess: (res, c) => {
      setToRevoke(null);
      setRevokeReason('');
      setRevokeNotice(describeRevoke(res, c.code ?? MASKED_CODE));
      void queryClient.invalidateQueries({ queryKey: ['admin-coupons'] });
    },
  });

  function closeRevoke(): void {
    setToRevoke(null);
    setRevokeReason('');
    revokeM.reset();
  }

  const revokingRedeemed = toRevoke?.status === 'redeemed';

  // Los planes gratis no admiten cupón (el backend también lo rechaza).
  const eligiblePlans = (plansQ.data ?? []).filter(
    (p) => p.status === 'active' && p.price_amount > 0,
  );

  function onCreate(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFormErr(null);
    const days = parseInt(freeDays, 10);
    const qty = mode === 'single' ? 1 : parseInt(quantity, 10);
    if (!partnerId || !planId) {
      setFormErr('Elegí partner y plan.');
      return;
    }
    if (!Number.isFinite(days) || days < 1 || days > 3650) {
      setFormErr('Los días gratis deben estar entre 1 y 3650.');
      return;
    }
    if (!Number.isFinite(qty) || qty < 1 || qty > 500) {
      setFormErr('La cantidad debe estar entre 1 y 500.');
      return;
    }
    createM.mutate({
      partner_id: partnerId,
      plan_id: planId,
      free_days: days,
      quantity: qty,
      ...(note.trim() ? { note: note.trim() } : {}),
    });
  }

  async function onDownloadPdf(): Promise<void> {
    if (!created) return;
    setDownloading(true);
    try {
      const partnerLabel =
        partnersQ.data?.find((p) => p.partner_id === created.partner_id)?.display_name ??
        created.partner_id;
      await downloadCouponsPdf({
        batch: created,
        partnerLabel,
        generatedBy: session?.display_name ?? 'admin',
      });
    } finally {
      setDownloading(false);
    }
  }

  async function onCopyAll(): Promise<void> {
    if (!created) return;
    await navigator.clipboard.writeText(created.coupons.map((c) => c.code).join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 data-help-page="coupons.page" data-help="coupons.page" className="text-xl font-semibold text-ink dark:text-ink-inverted">Cupones</h1>
        <p className="text-sm text-ink-muted">
          Códigos de un solo uso que activan un plan por una cantidad de días, gratis. Manejalos
          como plata: cualquiera que vea un código sin usar puede canjearlo.
        </p>
      </div>

      {/* --- Creación (nivel `create`) --- */}
      {canCreate ? (
        <section
          data-help="coupons.new"
          className="rounded-lg border border-surface-border p-4 dark:border-surface-dark-border"
        >
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-ink dark:text-ink-inverted">Crear cupones</h2>
            <div className="flex rounded-lg border border-surface-border text-sm dark:border-surface-dark-border">
              {(['single', 'batch'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={`px-3 py-1.5 first:rounded-l-lg last:rounded-r-lg ${
                    mode === m
                      ? 'bg-brand text-white'
                      : 'text-ink-muted hover:text-ink dark:hover:text-ink-inverted'
                  }`}
                >
                  {m === 'single' ? 'Cupón único' : 'Lote'}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={onCreate} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Partner">
              <select
                value={partnerId}
                onChange={(e) => {
                  setPartnerId(e.target.value);
                  setPlanId('');
                }}
                className={fieldCls()}
              >
                <option value="">Elegir…</option>
                {partnersQ.data?.map((p) => (
                  <option key={p.partner_id} value={p.partner_id}>
                    {p.display_name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Plan">
              <select
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                className={fieldCls()}
                disabled={!partnerId}
              >
                <option value="">{partnerId ? 'Elegir…' : 'Primero el partner'}</option>
                {eligiblePlans.map((p) => (
                  <option key={p.plan_id} value={p.plan_id}>
                    {p.display_name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Días gratis">
              <input
                type="number"
                min={1}
                max={3650}
                value={freeDays}
                onChange={(e) => setFreeDays(e.target.value)}
                className={fieldCls()}
              />
            </Field>
            {mode === 'batch' ? (
              <Field label="Cantidad">
                <input
                  type="number"
                  min={1}
                  max={500}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className={fieldCls()}
                />
              </Field>
            ) : null}
            <Field label="Nota (interna, opcional)">
              <input
                type="text"
                maxLength={500}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="ej: campaña lanzamiento julio"
                className={fieldCls()}
              />
            </Field>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={createM.isPending}
                className="min-h-[2.5rem] w-full rounded-lg bg-brand px-4 text-sm font-medium text-white hover:bg-brand-hover disabled:opacity-50"
              >
                {createM.isPending
                  ? 'Generando…'
                  : mode === 'single'
                    ? 'Crear cupón'
                    : 'Crear lote'}
              </button>
            </div>
          </form>
          {formErr ? <p className="mt-2 text-sm text-red-600">{formErr}</p> : null}

          {created ? (
            <div className="mt-4 rounded-lg border border-brand bg-brand/5 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-ink dark:text-ink-inverted">
                    {created.coupons.length === 1
                      ? 'Cupón creado'
                      : `${created.coupons.length} cupones creados`}{' '}
                    · {created.plan_name} · {created.free_days} días gratis
                  </p>
                  <p className="text-xs text-ink-muted">
                    Guardá el PDF si los vas a imprimir. Los códigos quedan también en el listado de
                    abajo.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => void onCopyAll()}
                    className="min-h-[2.25rem] rounded-lg border border-surface-border px-3 text-sm text-ink hover:bg-surface-border/30 dark:border-surface-dark-border dark:text-ink-inverted"
                  >
                    {copied ? 'Copiado ✓' : 'Copiar todos'}
                  </button>
                  <button
                    type="button"
                    onClick={() => void onDownloadPdf()}
                    disabled={downloading}
                    className="min-h-[2.25rem] rounded-lg bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover disabled:opacity-50"
                  >
                    {downloading ? 'Generando…' : 'Descargar PDF'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCreated(null)}
                    aria-label="Cerrar"
                    className="min-h-[2.25rem] rounded-lg px-2 text-sm text-ink-muted hover:text-ink dark:hover:text-ink-inverted"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                {created.coupons.map((c) => (
                  <code
                    key={c.coupon_id}
                    className="rounded border border-surface-border bg-surface px-2 py-1.5 text-center font-mono text-sm tracking-widest text-ink dark:border-surface-dark-border dark:bg-surface-dark dark:text-ink-inverted"
                  >
                    {c.code}
                  </code>
                ))}
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      {/* --- Listado --- */}
      <section className="rounded-lg border border-surface-border dark:border-surface-dark-border">
        <div className="flex flex-wrap gap-3 border-b border-surface-border p-3 dark:border-surface-dark-border">
          <Field label="Partner">
            <select value={fltPartner} onChange={(e) => changeFilter(() => setFltPartner(e.target.value))} className={fieldCls()}>
              <option value="">Todos</option>
              {partnersQ.data?.map((p) => (
                <option key={p.partner_id} value={p.partner_id}>
                  {p.display_name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Estado">
            <select value={fltStatus} onChange={(e) => changeFilter(() => setFltStatus(e.target.value))} className={fieldCls()}>
              <option value="">Todos</option>
              <option value="issued">Sin usar</option>
              <option value="redeemed">Canjeados</option>
              <option value="revoked">Anulados</option>
            </select>
          </Field>
          <Field label="Código exacto">
            <input
              type="text"
              value={fltCode}
              onChange={(e) => changeFilter(() => setFltCode(e.target.value))}
              placeholder="XXXXXXXX"
              className={fieldCls({ mono: true, extra: 'uppercase' })}
            />
          </Field>
        </div>

        {revokeNotice ? (
          <div className="p-3">
            <RetryNotice {...revokeNotice} onClose={() => setRevokeNotice(null)} />
          </div>
        ) : null}

        {listQ.isLoading ? <p className="p-4 text-sm text-ink-muted">Cargando…</p> : null}
        {listQ.isError ? (
          <p className="p-4 text-sm text-red-600">
            {listQ.error instanceof AdminApiError ? listQ.error.message : 'No se pudo cargar.'}
          </p>
        ) : null}

        {listQ.data ? (
          <div className="overflow-x-auto">
            <table data-help="coupons.list" className="w-full min-w-[52rem] text-sm">
              <thead>
                <tr className="border-b border-surface-border text-left text-xs uppercase text-ink-muted dark:border-surface-dark-border">
                  <th className="p-2">Código</th>
                  <th className="p-2">Plan</th>
                  <th className="p-2 text-right">Días</th>
                  <th className="p-2">Estado</th>
                  <th className="p-2">Creado por</th>
                  <th className="p-2">Canjeado por</th>
                  <th className="p-2">Nota</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {listQ.data.coupons.map((c) => (
                  <tr
                    key={c.coupon_id}
                    className="border-b border-surface-border/50 dark:border-surface-dark-border/50"
                  >
                    <td
                      className={
                        c.code
                          ? 'p-2 font-mono tracking-widest text-ink dark:text-ink-inverted'
                          : 'p-2 font-mono tracking-widest text-ink-muted'
                      }
                      title={c.code ? undefined : 'El código de un cupón vigente solo lo ve quien puede crearlos.'}
                    >
                      {c.code ?? MASKED_CODE}
                    </td>
                    <td className="p-2">{c.plan_name ?? c.plan_id}</td>
                    <td className="p-2 text-right">{c.free_days}</td>
                    <td className="p-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_BADGE[c.status].cls}`}>
                        {STATUS_BADGE[c.status].label}
                      </span>
                    </td>
                    <td className="p-2">
                      <div className="text-ink dark:text-ink-inverted">
                        {c.created_by_display_name ?? c.created_by.slice(0, 8)}
                      </div>
                      <div className="text-xs text-ink-muted">{c.created_at.slice(0, 10)}</div>
                    </td>
                    <td className="p-2">
                      {c.redeemed_by_user_id ? (
                        <Link
                          to={`/users/${c.redeemed_by_user_id}?partner_id=${encodeURIComponent(c.partner_id)}`}
                          className="text-brand underline hover:opacity-80"
                        >
                          {c.redeemed_by_display_name ?? c.redeemed_by_user_id.slice(0, 8)}
                        </Link>
                      ) : (
                        <span className="text-ink-muted">—</span>
                      )}
                    </td>
                    <td className="max-w-[12rem] truncate p-2 text-ink-muted" title={c.note ?? ''}>
                      {c.note ?? '—'}
                    </td>
                    <td className="p-2 text-right">
                      {(c.status === 'issued' || c.status === 'redeemed') && canRevoke ? (
                        <button
                          type="button"
                          onClick={() => {
                            setRevokeNotice(null);
                            setToRevoke(c);
                          }}
                          title={
                            c.status === 'redeemed'
                              ? 'Anular un cupón canjeado le saca a la persona el acceso del cupón: queda en el plan gratuito.'
                              : undefined
                          }
                          className="text-xs text-red-600 underline hover:opacity-80"
                        >
                          Anular
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
                {listQ.data.coupons.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-4 text-center text-ink-muted">
                      Sin cupones con esos filtros.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
            <div className="flex flex-wrap items-center justify-between gap-3 p-2 text-xs text-ink-muted">
              <span>
                {total.toLocaleString('es-AR')} en total
                {shown > 0
                  ? ` · mostrando ${(page * pageSize + 1).toLocaleString('es-AR')}–${(page * pageSize + shown).toLocaleString('es-AR')}`
                  : ''}
              </span>
              <span className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-2">
                  Por página
                  <select
                    value={pageSize}
                    onChange={(e) => changeFilter(() => setPageSize(Number(e.target.value)))}
                    className={fieldCls({ size: 'sm', block: false })}
                  >
                    {PAGE_SIZES.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
                <PageButton label="« Primera" disabled={page === 0} onClick={() => setPage(0)} />
                <PageButton
                  label="Anterior"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                />
                <span>
                  Página {page + 1} de {maxPage + 1}
                </span>
                <PageButton
                  label="Siguiente"
                  disabled={page >= maxPage}
                  onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
                />
                <PageButton
                  label="Última »"
                  disabled={page >= maxPage}
                  onClick={() => setPage(maxPage)}
                />
              </span>
            </div>
          </div>
        ) : null}
      </section>

      <ConfirmDialog
        open={toRevoke !== null}
        title={revokingRedeemed ? 'Anular un cupón canjeado' : 'Anular cupón'}
        description={
          <div className="space-y-3">
            {revokingRedeemed ? (
              <>
                <p>
                  El cupón <code className="font-mono">{toRevoke?.code ?? MASKED_CODE}</code> ya lo canjeó{' '}
                  <strong>{toRevoke?.redeemed_by_display_name ?? 'una persona'}</strong>. Al anularlo,{' '}
                  <strong>pierde ahora el acceso que le daba el cupón</strong> y su cuenta queda en el plan
                  gratuito del partner (si el partner no tiene plan gratuito, la suscripción queda expirada).
                </p>
                <p className="text-ink-subtle text-xs">
                  Si después del canje pagó o cambió de plan, se anula el cupón y su suscripción no se toca.
                  No hay nada que reintegrar: el cupón no se pagó. Para que no pueda entrar más, está el
                  bloqueo de cuenta en su ficha.
                </p>
                <label className="block text-sm">
                  <span className="font-medium">Motivo (requerido):</span>
                  <textarea
                    rows={2}
                    value={revokeReason}
                    maxLength={500}
                    required
                    onChange={(e) => setRevokeReason(e.target.value)}
                    placeholder="Ej.: el cupón se entregó a una persona que ya no corresponde"
                    className={fieldCls({ extra: 'mt-1' })}
                  />
                </label>
              </>
            ) : (
              <p>
                El cupón <code className="font-mono">{toRevoke?.code ?? MASKED_CODE}</code> deja de ser
                canjeable, para siempre.
              </p>
            )}
            {revokeM.isError ? (
              <p className="text-status-error text-xs">
                {revokeM.error instanceof AdminApiError ? revokeM.error.message : 'No se pudo anular el cupón.'}
              </p>
            ) : null}
          </div>
        }
        confirmLabel="Anular"
        danger
        loading={revokeM.isPending}
        onConfirm={() => {
          if (!toRevoke) return;
          if (revokingRedeemed && !revokeReason.trim()) return;
          revokeM.mutate(toRevoke);
        }}
        onCancel={closeRevoke}
      />
    </div>
  );
}

function PageButton({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="rounded border border-surface-border px-2 py-1 disabled:opacity-40 dark:border-surface-dark-border"
    >
      {label}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-ink-muted">{label}</span>
      {children}
    </label>
  );
}
